import { createApi } from '@reduxjs/toolkit/query/react';

import fetchBaseQuery from '@app/api/fetchBaseQuery';
import { constructUrl, mapObjectToFormData } from '@app/api/utils';

import type {
    AddCustomProjectFilterRequest,
    AddCustomProjectFilterResponse,
    CloseLanguageRequest,
    CloseLanguageResponse,
    CreateInheritedTaskRequest,
    CreateTaskRequest,
    DeleteCustomProjectFilterRequest,
    DeleteCustomProjectFilterResponse,
    FilterParam,
    GetActiveTasksRequest,
    GetActiveTasksResponse,
    GetBadgesResponse,
    GetProjectAffectedKeyCountRequest,
    GetProjectAffectedKeyCountResponse,
    GetProjectFilterDataResponse,
    GetProjectWordCountRequest,
    GetProjectWordCountResponse,
    GetTaskContributorsRequest,
    GetTaskContributorsResponse,
    GetTemplateTaskInfoRawResponse,
    GetTemplateTaskInfoResponse,
    GetTemplateTasksResponse,
    GetVendorsDataResponse,
} from './types';

export * from './types';

const getEncodedFilterParams = (filterParams: FilterParam[]) => {
    const textFilterParams: string[] = [];
    filterParams.forEach((filter, index) => {
        textFilterParams.push(`filter_params[${index}][object]=${filter.object}`);
        textFilterParams.push(`filter_params[${index}][option]=${filter.option}`);
        textFilterParams.push(`filter_params[${index}][relation]=${filter.relation}`);

        if (Array.isArray(filter.value)) {
            filter.value.forEach((value) => {
                textFilterParams.push(`filter_params[${index}][value][]=${value}`);
            });
        } else {
            textFilterParams.push(`filter_params[${index}][value]=${filter.value}`);
        }
    });

    return textFilterParams.map((param) => {
        const left = param.substring(0, param.indexOf('='));
        const right = param.substring(param.indexOf('=') + 1);

        return `${encodeURIComponent(left)}=${encodeURIComponent(right)}`;
    });
};

const tasksApi = createApi({
    reducerPath: 'lokaliseInternal_tasks',
    baseQuery: fetchBaseQuery(),
    tagTypes: ['TASKS_LIST', 'TASKS_BADGE'],
    endpoints: (builder) => ({
        getActiveTasks: builder.query<GetActiveTasksResponse, GetActiveTasksRequest>({
            query: ({ projectId } = {}) => ({
                method: 'GET',
                url: '/tasks/get-widget-data',
                params: projectId
                    ? {
                          // eslint-disable-next-line @typescript-eslint/naming-convention
                          current_project_id: projectId,
                      }
                    : {},
            }),
            providesTags: (result) =>
                result
                    ? [
                          ...result.map(({ id }) => ({ type: 'TASKS_LIST' as const, id })),
                          { type: 'TASKS_LIST', id: 'LIST' },
                      ]
                    : [{ type: 'TASKS_LIST', id: 'LIST' }],
        }),
        closeLanguage: builder.mutation<CloseLanguageResponse, CloseLanguageRequest>({
            query: ({ taskId, languageId }) => ({
                method: 'POST',
                url: '/tasks/close-lang',
                params: {
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    task_id: taskId,
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    lang_id: languageId,
                },
            }),
            invalidatesTags: [
                { type: 'TASKS_LIST', id: 'LIST' },
                { type: 'TASKS_BADGE', id: 'LIST' },
            ],
        }),
        getBadges: builder.query<GetBadgesResponse, void>({
            query: () => ({
                method: 'GET',
                url: '/tasks/badges',
            }),
            providesTags: [{ type: 'TASKS_BADGE', id: 'LIST' }],
        }),
        getTemplateTasks: builder.query<
            GetTemplateTasksResponse,
            { projectId: string; branchName: string | undefined }
        >({
            query: ({ projectId, branchName }) => ({
                method: 'GET',
                url: `/${projectId}/tasks/templates`,
                params: {
                    branch: branchName,
                },
            }),
        }),
        getTemplateTaskInfo: builder.query<GetTemplateTaskInfoResponse, { taskId: number }>({
            query: ({ taskId }) => ({
                method: 'GET',
                url: `/tasks/${taskId}/template-info`,
            }),
            transformResponse: (response: GetTemplateTaskInfoRawResponse): GetTemplateTaskInfoResponse => {
                const mappedTasks = response.tasks.map((task) => ({
                    ...task,
                    taskType: task.task_type,
                    options: {
                        autoCloseItems: task.options.auto_close_items,
                        autoCloseLanguages: task.options.auto_close_languages,
                        autoCloseTask: task.options.auto_close_task,
                        lockTranslations: task.options.do_lock_translations,
                        restrictCts: task.options.restrict_cts,
                        tagOnComplete: task.options.tag_on_complete,
                        saveAiTranslationToTm: task.options.save_ai_translation_to_tm,
                    },
                }));

                return {
                    ...response,
                    tasks: mappedTasks,
                };
            },
        }),
        getVendorsData: builder.query<GetVendorsDataResponse, void>({
            query: () => ({
                method: 'GET',
                url: `/tasks/vendors`,
            }),
        }),
        getProjectFilterData: builder.query<GetProjectFilterDataResponse, { projectId: string; branchName: string }>({
            query: ({ projectId, branchName }) => ({
                method: 'GET',
                url: `/project/${projectId}/filter/data`,
                params: {
                    branch: branchName,
                },
            }),
        }),
        /* eslint-disable @typescript-eslint/naming-convention */
        getProjectAffectedKeyCount: builder.query<
            GetProjectAffectedKeyCountResponse,
            GetProjectAffectedKeyCountRequest
        >({
            query: ({ projectId, branchName, filterParams }) => {
                const encodedFilterParams = getEncodedFilterParams(filterParams);

                return {
                    method: 'GET',
                    url: `${constructUrl(`/project/${projectId}/filter/get-maximum-count`, {
                        per_language: true,
                        branch: branchName,
                    })}&${encodedFilterParams.join('&')}`,
                };
            },
        }),
        addCustomProjectFilter: builder.mutation<AddCustomProjectFilterResponse, AddCustomProjectFilterRequest>({
            query: ({ projectId, name, filterParams }) => {
                const formData = new FormData();
                formData.append('name', name);

                filterParams.forEach((filter, index) => {
                    formData.append(`params[${index}][object]`, filter.object);
                    formData.append(`params[${index}][option]`, filter.option);
                    formData.append(`params[${index}][relation]`, filter.relation);
                    if (Array.isArray(filter.value)) {
                        filter.value.forEach((value) => {
                            formData.append(`params[${index}][value][]`, value);
                        });
                    } else {
                        formData.append(`params[${index}][value]`, filter.value.toString());
                    }
                });

                return {
                    method: 'POST',
                    url: `/project/${projectId}/filter/add`,
                    body: formData,
                    headers: {
                        'x-auto-content-type': '1',
                    },
                };
            },
        }),

        deleteCustomProjectFilter: builder.mutation<
            DeleteCustomProjectFilterResponse,
            DeleteCustomProjectFilterRequest
        >({
            query: ({ projectId, filterId }) => {
                const formData = new FormData();
                formData.append('id', filterId);

                return {
                    method: 'POST',
                    url: `/project/${projectId}/filter/delete`,
                    body: formData,
                    headers: {
                        'x-auto-content-type': '1',
                    },
                };
            },
        }),
        /* eslint-disable @typescript-eslint/naming-convention */
        getProjectWordCount: builder.query<GetProjectWordCountResponse, GetProjectWordCountRequest>({
            query: ({
                projectId,
                sourceLanguage,
                targetLanguages,
                parentTaskId,
                branchName,
                isMassTask,
                filterParams,
            }) => {
                const encodedFilterParams = getEncodedFilterParams(filterParams);
                const url = parentTaskId ? `/tasks/${parentTaskId}/lang-keys` : '/tasks/language-keys';

                return {
                    method: 'GET',
                    url: `${constructUrl(url, {
                        project_id: projectId,
                        source_lang_id: sourceLanguage,
                        target_lang_ids: targetLanguages,
                        branch: branchName ?? undefined,
                        is_mass: isMassTask ? 1 : 0,
                    })}&${encodedFilterParams.join('&')}`,
                };
            },
        }),

        getTaskContributors: builder.query<GetTaskContributorsResponse, GetTaskContributorsRequest>({
            query: ({
                projectId,
                sourceLanguage,
                targetLanguages,
                branchName,
                includeAdmins,
                onlyReviewers,
                isAi,
            }) => ({
                method: 'GET',
                url: constructUrl('/tasks/contributors-for-languages', {
                    projectId,
                    refLangId: sourceLanguage,
                    langIds: targetLanguages,
                    branch: branchName,
                    includeAdmins,
                    onlyReviewers,
                    isAi,
                }),
            }),
        }),
        createTask: builder.mutation<unknown, CreateTaskRequest>({
            query: (task) => ({
                method: 'POST',
                url: '/tasks/create',
                body: mapObjectToFormData({
                    // General
                    project_id: task.projectId,
                    branch: task.branchName,
                    title: task.title,
                    description: task.description,
                    due_date: task.dueDate,
                    type: task.type,
                    parent_task_id: task.parentTaskId,
                    // Options
                    auto_close_items: task.autoCloseItems,
                    autoclose_languages: task.autoCloseLanguages,
                    autoclose_task: task.autoCloseTask,
                    tags_to_add_after_close: task.tagOnCompleteTags,
                    do_lock_translations: task.lockTranslations,
                    is_cts_enabled: task.restrictCts,
                    custom_translation_status_ids: task.ctsIds,
                    template_title: task.useAsTemplateName,
                    save_ai_translation_to_tm: task.saveAiTranslationToTm,
                    // Scope
                    is_mass: task.isMassTask,
                    filter_params: task.filterParams,
                    source_lang_id: task.sourceLanguage,
                    langs: task.targetLanguages,
                    vrp_link_rate_cards: task.vendorRateCards,
                }),
                headers: {
                    'x-auto-content-type': '1',
                },
            }),
            invalidatesTags: [
                { type: 'TASKS_LIST', id: 'LIST' },
                { type: 'TASKS_BADGE', id: 'LIST' },
            ],
        }),
        createInheritedTask: builder.mutation<unknown, CreateInheritedTaskRequest>({
            query: ({ templateId, inheritedTasks, filterParams, isMassTask }) => ({
                method: 'POST',
                url: '/tasks/create-from-template',
                body: mapObjectToFormData({
                    inherit_item: inheritedTasks,
                    task_id: templateId,
                    project_filter: filterParams,
                    is_mass: isMassTask,
                }),
                headers: {
                    'x-auto-content-type': '1',
                },
            }),
            invalidatesTags: [
                { type: 'TASKS_LIST', id: 'LIST' },
                { type: 'TASKS_BADGE', id: 'LIST' },
            ],
        }),
    }),
});

export default tasksApi;

export const {
    useCloseLanguageMutation,
    useGetActiveTasksQuery,
    useLazyGetActiveTasksQuery,
    useGetBadgesQuery,
    useLazyGetBadgesQuery,
    useGetTemplateTaskInfoQuery,
    useGetProjectWordCountQuery,
    useLazyGetProjectWordCountQuery,
    useGetTaskContributorsQuery,
    useLazyGetTaskContributorsQuery,
    useGetTemplateTasksQuery,
    useLazyGetTemplateTaskInfoQuery,
    useGetVendorsDataQuery,
    useLazyGetVendorsDataQuery,
    useGetProjectFilterDataQuery,
    useGetProjectAffectedKeyCountQuery,
    useAddCustomProjectFilterMutation,
    useDeleteCustomProjectFilterMutation,
    useCreateTaskMutation,
    useCreateInheritedTaskMutation,
} = tasksApi;
